import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useResource, formatDate, titleCase } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { FilterBar, matches } from "../../components/FilterBar";
import { EntityForm, Field } from "../../components/EntityForm";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PrintDoc } from "../../components/PrintDoc";
import { Modal } from "../../components/Modal";
import type { Company, Invoice, InvoiceLine } from "../../lib/types";

const money = (value: string | number): string =>
    Number(value).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

const statusChip = (status: Invoice["status"]): string =>
    status === "paid" ? "pass" : status === "void" ? "mute"
    : status === "draft" ? "warn" : "info";

type Dialog =
    | { kind: "generate"; prefill?: { companyId: string; periodStart: string; periodEnd: string } }
    | { kind: "view"; invoice: Invoice }
    | { kind: "delete"; invoice: Invoice }
    | { kind: "void"; invoice: Invoice }
    | { kind: "pay"; invoice: Invoice }
    | null;

interface ReadyRow {
    company_id: string;
    company_name: string;
    shift_count: number;
    noted_count: number;
    hours: string;
    from_date: string;
    to_date: string;
}

/**
 * Invoices to the customer organisations. Lines are generated from delivered,
 * not-yet-invoiced shifts — the UNIQUE(shift_id) constraint in the database is
 * what makes double-billing structurally impossible, and this page just shows
 * that guarantee working.
 *
 * Partners see this same page read-only: RLS returns only their own issued
 * invoices, and the action buttons are manager-gated.
 */
interface PreviewShiftRow {
    shift_id: string;
    service_date?: string;
    work_date?: string;
    starts: string;
    ends: string;
    hours: string;
    employee_name?: string | null;
    participant_name?: string | null;
    placement_name?: string | null;
    role_name: string;
    rate_category: string;
    unit_price?: string;
    hourly_rate?: string;
    amount: string;
}

/**
 * The roster→invoice link, visible before committing: exactly the delivered,
 * not-yet-invoiced shifts the generate run will pick up. Same query as the
 * generator — if it shows here, it will be on the invoice.
 */
export const BillableShiftsPreview = ({ companyId, from, to, employeeId }: {
    companyId?: string; from: string; to: string; employeeId?: string;
}) => {
    const ready = Boolean((companyId || employeeId) && from && to);
    const path = !ready ? null
        : employeeId
            ? `/timesheets/preview?employeeId=${employeeId}&from=${from}&to=${to}`
            : `/invoices/preview?companyId=${companyId}&from=${from}&to=${to}`;
    const preview = useResource<PreviewShiftRow[]>(path);
    if (!ready) {
        return (
            <p style={{ fontSize: "0.78rem", color: "var(--muted)" }}>
                Choose {employeeId !== undefined ? "the worker" : "the organisation"} and both dates
                to see the roster shifts this will pick up.
            </p>
        );
    }
    if (preview.error) return <div className="error-banner">{preview.error}</div>;
    const rows = preview.data ?? [];
    const total = rows.reduce((sum, r) => sum + Number(r.amount), 0);
    return (
        <div className="card" style={{ boxShadow: "none" }}>
            <div className="card-head">
                <h2 style={{ fontSize: "0.9rem" }}>From the roster</h2>
                <span className={`chip ${rows.length > 0 ? "info" : "warn"}`}>
                    {rows.length} delivered shift{rows.length === 1 ? "" : "s"} · {money(total)}
                </span>
            </div>
            <div className="card-body flush"><div className="table-wrap">
                <table>
                    <thead><tr><th>Date</th><th>Time</th><th>Who</th><th>Role</th>
                        <th style={{ textAlign: "right" }}>Hours</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.shift_id}>
                                <td className="mono">{formatDate(row.service_date ?? row.work_date ?? "")}</td>
                                <td className="mono">{row.starts}–{row.ends}</td>
                                <td style={{ fontWeight: 600 }}>
                                    {row.employee_name ?? row.placement_name ?? "—"}
                                    {row.participant_name && (
                                        <div style={{ fontSize: "0.72rem", color: "var(--muted)", fontWeight: 400 }}>
                                            for {row.participant_name}
                                        </div>
                                    )}
                                </td>
                                <td style={{ fontSize: "0.8rem" }}>
                                    {row.role_name}
                                    {row.rate_category !== "weekday_day" && (
                                        <span className="chip mute" style={{ marginLeft: 4 }}>
                                            {row.rate_category.replace(/_/g, " ")}
                                        </span>
                                    )}
                                </td>
                                <td className="mono" style={{ textAlign: "right" }}>{Number(row.hours).toFixed(2)}</td>
                                <td className="mono" style={{ textAlign: "right", fontWeight: 600 }}>{money(row.amount)}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="empty">
                                No delivered, unbilled shifts in this period — nothing to generate.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div></div>
        </div>
    );
};

export const Invoices = () => {
    const { user, mayQuality } = useAuth();
    // Engine adoption (9 Aug 2026): the matrix answers instead of the role,
    // so a reasoned, expiring grant shows the same buttons it unlocks
    // server-side. Role defaults mirror the old admin/manager list.
    const canManage = ["invoices.generate", "invoices.issue", "invoices.settle", "invoices.void"]
        .some((key) => mayQuality(key));
    const isPartner = user?.role === "partner";

    const invoices = useResource<Invoice[]>("/invoices");
    const companies = useResource<Company[]>(canManage ? "/companies" : null);
    const ready = useResource<ReadyRow[]>(canManage ? "/invoices/ready" : null);

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>(() => {
        const params = new URLSearchParams(window.location.search);
        const company = params.get("company");
        return (company ? { company } : {}) as Record<string, string>;
    });
    const [dialog, setDialog] = useState<Dialog>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const refresh = () => { invoices.reload(); ready.reload(); setDialog(null); };

    const transition = async (invoice: Invoice, action: string) => {
        setBusyId(invoice.id);
        setActionError(null);
        try {
            await api.post(`/invoices/${invoice.id}/${action}`, {});
            invoices.reload();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Action failed");
        } finally {
            setBusyId(null);
        }
    };

    const generateFields: Field[] = [
        { name: "companyId", label: "Bill to organisation", kind: "select", required: true,
          options: (companies.data ?? []).map((c) => ({ value: c.id, label: c.name })) },
        { name: "periodStart", label: "Period from", kind: "date", required: true, half: true },
        { name: "periodEnd", label: "Period to", kind: "date", required: true, half: true },
        { name: "dueInDays", label: "Payment terms (days)", kind: "number", min: 0, max: 90, half: true,
          help: "Blank means 14 days." },
        { name: "discountPercent", label: "Discount %", kind: "number", min: 0, max: 100, half: true,
          help: "Applied to the subtotal before GST." },
        { name: "addGst", label: "Add GST (10%) — NDIS supports are usually GST-free", kind: "checkbox" },
        { name: "notes", label: "Notes shown on the invoice", kind: "textarea" },
    ];

    const all = invoices.data ?? [];
    const filtered = all.filter((invoice) =>
        matches(search, invoice.invoice_number, invoice.bill_to_name) &&
        (!filters.status || invoice.status === filters.status) &&
        (!filters.company || invoice.company_id === filters.company)
    );

    if (invoices.error) return <div className="error-banner">{invoices.error}</div>;

    return (
        <div>
            {dialog?.kind === "generate" && (
                <EntityForm
                    title="Generate Invoice" path="/invoices/generate" fields={generateFields}
                    initial={dialog.prefill}
                    subtitle="Built from delivered shifts in the period that have not been invoiced before, at each role's bill rate. NDIS supports default to GST-free."
                    preview={(values) => (
                        <BillableShiftsPreview
                            companyId={String(values.companyId ?? "")}
                            from={String(values.periodStart ?? "")}
                            to={String(values.periodEnd ?? "")} />
                    )}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "view" && (
                <InvoiceDoc invoice={dialog.invoice} onClose={() => setDialog(null)} />
            )}
            {dialog?.kind === "delete" && (
                <ConfirmDialog
                    title={`Delete draft ${dialog.invoice.invoice_number}?`}
                    tone="danger"
                    message="The draft is removed and its shifts become billable again on the next run."
                    warning="Only drafts can be deleted. An issued invoice is a document your customer may hold — those are voided, never erased."
                    confirmLabel="Delete draft"
                    request={{ method: "delete", path: `/invoices/${dialog.invoice.id}` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "pay" && (
                <PayDialog invoice={dialog.invoice} onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "void" && (
                <ConfirmDialog
                    title={`Void ${dialog.invoice.invoice_number}?`}
                    tone="danger"
                    message="The invoice is marked void and drops out of amounts owing."
                    warning="The document itself is kept — your customer may have received it, so the record of what was issued must survive. Its shifts stay attached and will not be re-billed automatically."
                    confirmLabel="Void invoice"
                    request={{ method: "post", path: `/invoices/${dialog.invoice.id}/void` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}

            {actionError && <div className="error-banner">{actionError}</div>}

            {canManage && (ready.data ?? []).length > 0 && (
                <div className="card" style={{ marginBottom: "0.85rem" }}>
                    <div className="card-head">
                        <div>
                            <h2>Ready to invoice</h2>
                            <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: "0.15rem" }}>
                                Delivered shifts nothing has billed yet, per provider. The note count shows
                                which already have their progress note — done work, documented, waiting to be drafted.
                            </p>
                        </div>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Provider</th><th>Unbilled shifts</th><th>With note</th>
                                        <th>Hours</th><th>Span</th><th style={{ textAlign: "right" }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(ready.data ?? []).map((row) => (
                                        <tr key={row.company_id}>
                                            <td style={{ fontWeight: 600 }}>{row.company_name}</td>
                                            <td className="mono">{row.shift_count}</td>
                                            <td>
                                                <span className={`chip ${row.noted_count === row.shift_count ? "pass" : "warn"}`}>
                                                    {row.noted_count}/{row.shift_count}
                                                </span>
                                            </td>
                                            <td className="mono">{Number(row.hours).toFixed(1)}</td>
                                            <td className="mono">{formatDate(row.from_date)} – {formatDate(row.to_date)}</td>
                                            <td>
                                                <div className="row-actions">
                                                    <button className="icon-btn" onClick={() => setDialog({
                                                        kind: "generate",
                                                        prefill: {
                                                            companyId: row.company_id,
                                                            periodStart: row.from_date,
                                                            periodEnd: row.to_date,
                                                        },
                                                    })}>
                                                        Draft invoice
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search invoices…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    {
                        name: "status", allLabel: "All Status",
                        options: ["draft", "issued", "sent", "paid", "void"].map((s) => ({ value: s, label: titleCase(s) })),
                    },
                    ...(canManage ? [{
                        name: "company", allLabel: "All Organisations",
                        options: (companies.data ?? []).map((c) => ({ value: c.id, label: c.name })),
                    }] : []),
                ]}
                action={canManage && (
                    <button className="btn" onClick={() => setDialog({ kind: "generate" })}>+ Generate Invoice</button>
                )}
            />

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Invoice</th><th>Bill to</th><th>Period</th>
                                    <th>Total</th><th>Status</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((invoice) => (
                                    <tr key={invoice.id}>
                                        <td className="mono" style={{ fontWeight: 600 }}>{invoice.invoice_number}</td>
                                        <td>
                                            {canManage ? (
                                                <Link to={`/participants?provider=${invoice.company_id}`}
                                                    title="Their participants">{invoice.bill_to_name}</Link>
                                            ) : invoice.bill_to_name}
                                        </td>
                                        <td className="mono">
                                            {formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}
                                        </td>
                                        <td className="mono" style={{ fontWeight: 600 }}>{money(invoice.total)}</td>
                                        <td>
                                            <span className={`chip ${statusChip(invoice.status)}`}>{invoice.status}</span>
                                            {(invoice.pending_claims ?? 0) > 0 && (
                                                <span className="chip warn" style={{ marginLeft: 4 }}>payment claimed</span>
                                            )}
                                            {invoice.due_date && invoice.status !== "paid" && invoice.status !== "void" && (
                                                <div style={{ fontSize: "0.7rem", color: "var(--muted)", marginTop: 2 }}>
                                                    due {formatDate(invoice.due_date)}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="row-actions">
                                                <button className="icon-btn" onClick={() => setDialog({ kind: "view", invoice })}>
                                                    View / PDF
                                                </button>
                                                {isPartner && (invoice.status === "issued" || invoice.status === "sent") && (
                                                    <button className="icon-btn" style={{ fontWeight: 700 }}
                                                        onClick={() => setDialog({ kind: "pay", invoice })}>
                                                        Pay
                                                    </button>
                                                )}
                                                {canManage && (invoice.pending_claims ?? 0) > 0 && (
                                                    <button className="icon-btn" disabled={busyId === invoice.id}
                                                        onClick={() => void transition(invoice, "confirm-payment")}>
                                                        Confirm payment
                                                    </button>
                                                )}
                                                {canManage && invoice.status === "draft" && (
                                                    <>
                                                        <button className="icon-btn" disabled={busyId === invoice.id}
                                                            onClick={() => void transition(invoice, "issue")}>Issue</button>
                                                        <button className="icon-btn danger"
                                                            onClick={() => setDialog({ kind: "delete", invoice })}>Delete</button>
                                                    </>
                                                )}
                                                {canManage && invoice.status === "issued" && (
                                                    <button className="icon-btn" disabled={busyId === invoice.id}
                                                        onClick={() => void transition(invoice, "mark-sent")}>Mark sent</button>
                                                )}
                                                {canManage && (invoice.status === "issued" || invoice.status === "sent") && (
                                                    <>
                                                        <button className="icon-btn" disabled={busyId === invoice.id}
                                                            onClick={() => void transition(invoice, "mark-paid")}>Mark paid</button>
                                                        <button className="icon-btn danger"
                                                            onClick={() => setDialog({ kind: "void", invoice })}>Void</button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={6} className="empty">
                                        {all.length === 0 ? "No invoices yet. Generate one from delivered shifts." : "No invoices match."}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const InvoiceDoc = ({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) => {
    const detail = useResource<Invoice & { lines: InvoiceLine[] }>(`/invoices/${invoice.id}`);
    const data = detail.data;

    return (
        <PrintDoc
            kind="TAX INVOICE" number={invoice.invoice_number} onClose={onClose}
            emailTo={invoice.bill_to_email}
            emailSubject={`${invoice.invoice_number} — ${invoice.bill_to_name}`}
        >
            {!data ? <p>Loading…</p> : (
                <>
                    <div className="doc-meta">
                        <div>
                            <div className="doc-label">Bill to</div>
                            <div style={{ fontWeight: 700 }}>{data.bill_to_name}</div>
                            {data.bill_to_email && <div>{data.bill_to_email}</div>}
                        </div>
                        <div>
                            <div className="doc-label">Details</div>
                            <div>Issued: {formatDate(data.issue_date)}</div>
                            <div>Due: {formatDate(data.due_date)}</div>
                            <div>Period: {formatDate(data.period_start)} – {formatDate(data.period_end)}</div>
                        </div>
                    </div>

                    <table className="doc-table">
                        <thead>
                            <tr><th>Date</th><th>Description</th><th className="num">Hours</th><th className="num">Rate</th><th className="num">Amount</th></tr>
                        </thead>
                        <tbody>
                            {data.lines.map((line) => (
                                <tr key={line.id}>
                                    <td>{formatDate(line.service_date)}</td>
                                    <td>{line.description}</td>
                                    <td className="num">{Number(line.hours).toFixed(2)}</td>
                                    <td className="num">{money(line.unit_price)}</td>
                                    <td className="num">{money(line.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr><td colSpan={4} className="num">Subtotal</td><td className="num">{money(data.subtotal)}</td></tr>
                            {Number(data.discount_amount) > 0 && (
                                <tr>
                                    <td colSpan={4} className="num">Discount ({Number(data.discount_percent).toFixed(0)}%)</td>
                                    <td className="num">-{money(data.discount_amount)}</td>
                                </tr>
                            )}
                            <tr>
                                <td colSpan={4} className="num">
                                    GST {Number(data.gst_rate) === 0 ? "(GST-free NDIS supports)" : `(${(Number(data.gst_rate) * 100).toFixed(0)}%)`}
                                </td>
                                <td className="num">{money(data.gst_amount)}</td>
                            </tr>
                            <tr className="doc-total"><td colSpan={4} className="num">Total due</td><td className="num">{money(data.total)}</td></tr>
                        </tfoot>
                    </table>

                    {data.notes && <p className="doc-notes">{data.notes}</p>}
                </>
            )}
        </PrintDoc>
    );
};


/**
 * The pay flow, honestly: no card gateway is connected yet, so paying means
 * transferring by the instructions shown (with the invoice number as the
 * reference) and telling us you have. A manager confirms the money landed —
 * that confirmation is what flips the invoice to paid. When a gateway is
 * added it writes the same confirmation via webhook and this dialog gains a
 * card button; nothing else changes.
 */
const PayDialog = ({ invoice, onClose, onDone }: {
    invoice: Invoice; onClose: () => void; onDone: () => void;
}) => {
    const billing = useResource<{ paymentInstructions: string }>("/billing-details");
    const [reference, setReference] = useState(invoice.invoice_number);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    const claim = async () => {
        setBusy(true);
        setError(null);
        try {
            await api.post(`/invoices/${invoice.id}/claim-payment`, { reference: reference.trim() });
            setDone(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not record the payment");
            setBusy(false);
        }
    };

    return (
        <Modal title={`Pay ${invoice.invoice_number}`}
            subtitle={`${money(invoice.total)} to ${invoice.bill_to_name === "" ? "TrustCare" : "us"} — due ${invoice.due_date ? new Date(invoice.due_date).toLocaleDateString("en-AU") : "on receipt"}`}
            onClose={onClose}
            footer={done ? (
                <button className="btn" onClick={onDone}>Close</button>
            ) : (
                <>
                    <button className="btn" onClick={() => void claim()} disabled={busy || reference.trim().length < 2}>
                        {busy ? "Recording…" : "I have paid — notify TrustCare"}
                    </button>
                    <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
                </>
            )}>
            {error && <div className="error-banner">{error}</div>}
            {done ? (
                <p className="confirm-message">
                    Thank you — your payment advice for {money(invoice.total)} has been recorded.
                    The invoice will show as paid once the payment is confirmed on our side.
                </p>
            ) : (
                <>
                    <div className="field">
                        <label>How to pay</label>
                        <div className="asked-q-answer" style={{ whiteSpace: "pre-wrap" }}>
                            {billing.data?.paymentInstructions ?? "Loading…"}
                        </div>
                    </div>
                    <div className="field">
                        <label htmlFor="pay-ref">Payment reference you used</label>
                        <input id="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
                        <span className="help">Use the invoice number as the reference so we can match it quickly.</span>
                    </div>
                </>
            )}
        </Modal>
    );
};
